/**
 * Pixel / container ID validation (TAG-1871).
 *
 * A merchant pasted an entire TikTok `<script>` snippet into the "Pixel ID"
 * field of the store-level integration. The step-config seeder split that value
 * on `[;,]` into 56 "pixels" — every one of them a JS fragment — and the
 * storefront then handed each fragment to `ttq.load()`, which pinned the
 * renderer at 100% CPU before first paint and blanked the checkout for days.
 *
 * Two tiers, on purpose:
 *
 *  - `isSafePixelId` is the HARD rule. Every ID format we support (Meta digits,
 *    TikTok alphanumerics, Snapchat alphanumerics + dashes, Pinterest digits,
 *    `GTM-`/`AW-`/`G-` containers) is drawn from `[A-Za-z0-9_-]`, so anything
 *    carrying markup, quotes, parentheses, spaces or newlines is junk by
 *    construction. Blocking in the UI, and enforced again independently by the
 *    storefront SDK so configs that are ALREADY polluted can never reach a
 *    third-party loader.
 *
 *  - The per-provider `pixelIdRegex` in `constants/tracking.ts` is the SOFT
 *    rule, surfaced as a warning by the forms rather than an error: those
 *    patterns describe the ID formats we know about today, and a merchant with
 *    an unusual-but-working ID must not be locked out of their own config.
 */

/** Longest ID we accept. Real IDs top out around 20 chars; 64 is slack. */
export const MAX_PIXEL_ID_LENGTH = 64;

/**
 * Upper bound on IDs parsed out of one separator-delimited field. A merchant
 * running more than this many pixels on a single provider is not a case we've
 * ever seen — a value that produces more is a paste accident, not a config.
 */
export const MAX_PIXEL_IDS_PER_PROVIDER = 20;

/** The only shape any supported pixel / container ID takes. */
export const SAFE_PIXEL_ID_PATTERN = new RegExp(`^[A-Za-z0-9_-]{1,${MAX_PIXEL_ID_LENGTH}}$`);

/**
 * Markers of a pasted snippet rather than an ID. Checked against the WHOLE
 * field before any splitting, so a script is rejected outright instead of being
 * shredded into fragments that each look individually plausible.
 */
const PASTED_SNIPPET_PATTERN = /<|>|<!--|[\r\n\t]/;

/** Separators the multi-pixel fields accept (documented as `;` in the UI). */
const PIXEL_ID_SEPARATOR = /[;,]/;

export type PixelIdProblem = 'required' | 'pastedSnippet' | 'tooLong' | 'invalidCharacters';

/** True when `value` is a single, well-formed pixel / container ID. */
export function isSafePixelId(value: unknown): value is string {
  return typeof value === 'string' && SAFE_PIXEL_ID_PATTERN.test(value.trim());
}

/** True when `value` carries markup or newlines — i.e. a pasted snippet. */
export function looksLikePastedSnippet(value: string): boolean {
  return PASTED_SNIPPET_PATTERN.test(value);
}

/**
 * Classify a single-ID field. Returns `null` when the value is acceptable.
 * Callers map the code to a message (the tracking forms use literal English
 * strings, matching the rest of their zod schemas).
 */
export function pixelIdProblem(value: string | undefined | null): PixelIdProblem | null {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return 'required';
  if (looksLikePastedSnippet(trimmed)) return 'pastedSnippet';
  if (trimmed.length > MAX_PIXEL_ID_LENGTH) return 'tooLong';
  if (!SAFE_PIXEL_ID_PATTERN.test(trimmed)) return 'invalidCharacters';
  return null;
}

export const PIXEL_ID_PROBLEM_MESSAGES: Record<PixelIdProblem, string> = {
  required: 'Pixel ID is required',
  pastedSnippet:
    'This looks like a pasted tracking script. Enter only the pixel ID itself — not the <script> snippet.',
  tooLong: `Pixel ID must be at most ${MAX_PIXEL_ID_LENGTH} characters`,
  invalidCharacters: 'Pixel ID may only contain letters, digits, hyphens and underscores',
};

/** Convenience wrapper: the message for a single-ID field, or `null`. */
export function pixelIdErrorMessage(value: string | undefined | null): string | null {
  const problem = pixelIdProblem(value);
  return problem ? PIXEL_ID_PROBLEM_MESSAGES[problem] : null;
}

export interface ParsedPixelIds {
  /** Well-formed IDs, in field order, capped at `MAX_PIXEL_IDS_PER_PROVIDER`. */
  ids: string[];
  /** Non-empty fragments that failed the hard rule and were dropped. */
  rejected: string[];
  /** True when more than `MAX_PIXEL_IDS_PER_PROVIDER` valid IDs were present. */
  truncated: boolean;
}

/**
 * Parse a separator-delimited pixel ID field into usable IDs.
 *
 * A field carrying markup or newlines is rejected WHOLE — splitting it first is
 * exactly what turned one bad paste into 56 junk pixels. Everything else is
 * split on `[;,]`, trimmed, and filtered through the hard rule.
 */
export function parsePixelIds(raw: string | undefined | null): ParsedPixelIds {
  const value = (raw ?? '').trim();
  if (!value) return { ids: [], rejected: [], truncated: false };
  if (looksLikePastedSnippet(value)) return { ids: [], rejected: [value], truncated: false };

  const ids: string[] = [];
  const rejected: string[] = [];

  for (const fragment of value.split(PIXEL_ID_SEPARATOR)) {
    const candidate = fragment.trim();
    if (!candidate) continue;
    if (isSafePixelId(candidate)) ids.push(candidate);
    else rejected.push(candidate);
  }

  const truncated = ids.length > MAX_PIXEL_IDS_PER_PROVIDER;
  return { ids: truncated ? ids.slice(0, MAX_PIXEL_IDS_PER_PROVIDER) : ids, rejected, truncated };
}

/**
 * Validate a separator-delimited field (store-level integration settings).
 * Returns a message, or `null` when every fragment is a well-formed ID.
 */
export function pixelIdListErrorMessage(raw: string | undefined | null): string | null {
  const value = (raw ?? '').trim();
  if (!value) return PIXEL_ID_PROBLEM_MESSAGES.required;

  const { ids, rejected, truncated } = parsePixelIds(value);
  if (rejected.length > 0) {
    // A whole-field rejection means a pasted snippet; a per-fragment one means
    // the merchant mistyped or included something that isn't an ID.
    return looksLikePastedSnippet(value)
      ? PIXEL_ID_PROBLEM_MESSAGES.pastedSnippet
      : `Not a valid pixel ID: "${truncateForMessage(rejected[0]!)}"`;
  }
  if (ids.length === 0) return PIXEL_ID_PROBLEM_MESSAGES.required;
  if (truncated) return `At most ${MAX_PIXEL_IDS_PER_PROVIDER} pixel IDs are supported`;
  return null;
}

function truncateForMessage(value: string): string {
  return value.length > 40 ? `${value.slice(0, 40)}…` : value;
}
